import { Link } from "react-router-dom";
import { typeLabels, type TitleItem } from "@/lib/catalog";
import { useFavorites } from "@/lib/favorites";

interface PosterCardProps {
  item: TitleItem;
  showSynopsis?: boolean;
  compact?: boolean;
}

export default function PosterCard({ item, showSynopsis = false, compact = false }: PosterCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorite = isFavorite(item.id);

  return (
    <article className="group relative flex flex-col">
      <Link
        to={`/detail/${item.id}`}
        className="block relative overflow-hidden rounded-lg bg-background-200"
        aria-label={`${item.title} 详情`}
      >
        <div className={`relative w-full ${compact ? "h-[220px] sm:h-[260px]" : "h-[260px] sm:h-[330px]"} overflow-hidden`}>
          <img
            src={item.poster}
            alt={`${item.title} 海报`}
            title={`${item.title} ${typeLabels[item.type]} 日韩影视`}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent opacity-80" />

          <div className="absolute left-3 top-3 flex items-center gap-1.5">
            <span className="px-2 py-0.5 rounded-md bg-background-50/90 text-[11px] font-label font-semibold text-foreground-900">
              {typeLabels[item.type]}
            </span>
            {item.isNew && (
              <span className="px-2 py-0.5 rounded-md bg-accent-500 text-[11px] font-label font-semibold text-background-50">
                NEW
              </span>
            )}
          </div>

          <div className="absolute right-3 top-3 flex items-center gap-1 px-2 py-0.5 rounded-md bg-foreground-950/60 backdrop-blur-sm">
            <i className="ri-star-fill text-accent-400 text-[11px] w-[11px] h-[11px] flex items-center justify-center" />
            <span className="text-[11px] font-label font-semibold text-background-50">{item.rating.toFixed(1)}</span>
          </div>

          <div className="absolute inset-x-0 bottom-0 p-3 translate-y-2 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-500 text-background-50 text-xs font-label font-semibold whitespace-nowrap">
              <i className="ri-play-fill w-3.5 h-3.5 flex items-center justify-center" />
              立即观看
            </span>
          </div>
        </div>
      </Link>

      <button
        type="button"
        onClick={() => toggleFavorite(item.id)}
        aria-label={favorite ? "取消收藏" : "收藏"}
        className={`absolute top-14 right-3 z-10 w-8 h-8 rounded-md flex items-center justify-center cursor-pointer transition-colors ${
          favorite
            ? "bg-accent-500 text-background-50"
            : "bg-background-50/85 text-foreground-800 hover:bg-background-50"
        }`}
      >
        <i className={`${favorite ? "ri-heart-fill" : "ri-heart-line"} text-sm w-4 h-4 flex items-center justify-center`} />
      </button>

      <div className="pt-3 flex flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/detail/${item.id}`}
            className="font-heading text-[15px] font-bold text-foreground-950 leading-snug line-clamp-1 hover:text-primary-600 transition-colors"
          >
            {item.title}
          </Link>
          <span className="shrink-0 text-xs font-label text-foreground-500 mt-0.5">{item.year}</span>
        </div>
        <p className="text-xs text-foreground-600 flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <span>{item.region}</span>
          <span className="text-foreground-400">·</span>
          <span>{item.genre.slice(0, 2).join(" / ")}</span>
        </p>
        {showSynopsis && (
          <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mt-0.5">{item.synopsis}</p>
        )}
      </div>
    </article>
  );
}